import { useState } from 'react';
import clsx from 'clsx';
import type { Vendor } from '@shopgnd/shared';
import { useResolvedMediaUrl } from '@/lib/mediaUrl';
import { resolveVendorBrandColor } from '@/lib/vendorBrand';
import { externalImageProps } from '@/lib/externalImage';

export function vendorInitials(name: string) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

const sizeClasses = {
  sm: {
    box: 'h-8 w-8 rounded-md text-xs sm:h-12 sm:w-12 sm:rounded-lg sm:text-sm',
    pad: 'p-0.5 sm:p-1',
    wide: false,
    img: { width: 48, height: 48 },
  },
  md: {
    box: 'h-10 w-10 rounded-lg text-xs sm:h-16 sm:w-16 sm:rounded-xl sm:text-base',
    pad: 'p-1 sm:p-1.5',
    wide: false,
    img: { width: 64, height: 64 },
  },
  lg: {
    box: 'h-12 w-12 rounded-lg text-base sm:h-24 sm:w-24 sm:rounded-2xl sm:text-2xl',
    pad: 'p-1 sm:p-2',
    wide: false,
    img: { width: 96, height: 96 },
  },
  xl: {
    box: 'h-16 w-16 rounded-xl text-lg sm:h-28 sm:w-28 sm:rounded-2xl sm:text-3xl md:h-32 md:w-32',
    pad: 'p-1.5 sm:p-3',
    wide: false,
    img: { width: 128, height: 128 },
  },
  /** Storefront header — wide wordmarks; container hugs image (no flex stretch). */
  store: {
    box: 'h-10 max-h-10 w-fit max-w-[min(100%,280px)] rounded-lg sm:h-20 sm:max-h-20 sm:max-w-[min(100%,320px)] sm:rounded-xl md:h-24 md:max-h-24',
    pad: 'px-2 py-1 sm:px-4 sm:py-2',
    wide: true,
    img: { width: 280, height: 96 },
  },
};

export function VendorLogo({
  vendor,
  size = 'md',
  className,
}: {
  vendor: Pick<Vendor, 'businessName' | 'logoUrl' | 'brandColor'>;
  size?: keyof typeof sizeClasses;
  className?: string;
}) {
  const { box, pad, wide, img: imgSize } = sizeClasses[size];
  const brandColor = resolveVendorBrandColor(vendor);
  const logoSrc = useResolvedMediaUrl(vendor.logoUrl);
  const [imgFailed, setImgFailed] = useState(false);

  if (logoSrc && !imgFailed) {
    return (
      <div
        className={clsx(
          box,
          'flex shrink-0 items-center justify-center self-start bg-white ring-2 ring-white/20',
          wide ? 'min-w-0' : 'aspect-square overflow-hidden',
          pad,
          className,
        )}
      >
        <img
          src={logoSrc}
          alt={`${vendor.businessName} logo`}
          width={imgSize.width}
          height={imgSize.height}
          decoding="async"
          className="block h-full w-auto max-w-full object-contain object-center"
          onError={() => setImgFailed(true)}
          {...externalImageProps(logoSrc)}
        />
      </div>
    );
  }

  return (
    <div
      className={clsx(
        box,
        'flex shrink-0 items-center justify-center self-start font-bold text-white ring-2 ring-white/20',
        wide ? 'min-w-0' : 'aspect-square',
        className,
      )}
      style={{ backgroundColor: brandColor }}
      aria-hidden
    >
      {vendorInitials(vendor.businessName)}
    </div>
  );
}
